import { type ChatItemDto, type PublicRoomState, type RoomFx } from '@spin/shared';
import { create } from 'zustand';

export type FxEvent = RoomFx & { v: number; at: number; id: number };

interface RoomStore {
  room: PublicRoomState | null;
  chat: ChatItemDto[];
  /** serverNow − Date.now(), smoothed; deadlines from the server are converted with it. */
  offset: number;
  fx: FxEvent[];
  applySnapshot: (room: PublicRoomState | null, chat: ChatItemDto[], serverNow: number) => void;
  applyState: (room: PublicRoomState, serverNow: number) => void;
  pushFx: (fx: RoomFx & { v: number }) => void;
  consumeFx: (id: number) => void;
  addChat: (item: ChatItemDto) => void;
  clear: () => void;
}

let fxSeq = 0;
const CHAT_LIMIT = 80;

export const useRoom = create<RoomStore>((set) => ({
  room: null,
  chat: [],
  offset: 0,
  fx: [],
  applySnapshot: (room, chat, serverNow) =>
    set((s) => ({ room, chat: chat.slice(-CHAT_LIMIT), offset: smooth(s.offset, serverNow), fx: [] })),
  applyState: (room, serverNow) =>
    set((s) => {
      // States arrive over several channels (ack + broadcast); never go back in time.
      if (s.room && s.room.id === room.id && room.v <= s.room.v) return {};
      return { room, offset: smooth(s.offset, serverNow) };
    }),
  pushFx: (fx) => set((s) => ({ fx: [...s.fx, { ...fx, at: Date.now(), id: ++fxSeq }].slice(-20) })),
  consumeFx: (id) => set((s) => ({ fx: s.fx.filter((f) => f.id !== id) })),
  addChat: (item) =>
    set((s) => (s.chat.some((c) => c.id === item.id) ? {} : { chat: [...s.chat, item].slice(-CHAT_LIMIT) })),
  clear: () => set({ room: null, chat: [], fx: [] }),
}));

function smooth(prev: number, serverNow: number): number {
  const sample = serverNow - Date.now();
  return prev === 0 ? sample : prev * 0.7 + sample * 0.3;
}

/** Current server time as seen by this client. */
export function serverNow(): number {
  return Date.now() + useRoom.getState().offset;
}
